<script setup lang="ts">
import { nextTick, ref, shallowRef, watch } from 'vue';
import { useFloatingPanel } from '../Popover/useFloatingPanel';
import { findPanelAnchor, focusPanel, panelIdOf } from './panel';
import type { NavBarNode } from './types';

const props = defineProps<{
  /** The root entry whose `panel` component this renders. */
  node: NavBarNode;
  /** Whether the panel is currently open. */
  open: boolean;
}>();

const emit = defineEmits<{
  /** The content asked to close: the NavBar closes and refocuses the entry. */
  close: [];
}>();

const panelRef = ref<HTMLElement | null>(null);

/**
 * The entry's wrapper, looked up on each open: the panel sits outside the
 * menubar, and the entry may have moved between the rows. Positioned in JS like
 * NxPopover, not with CSS anchor positioning, which Safari ignores (I-3820).
 */
const anchorRef = shallowRef<HTMLElement | null>(null);
const { floatingStyles } = useFloatingPanel(anchorRef, panelRef, {
  open: () => props.open,
  placement: 'bottom-start',
  offset: 0,
  fitHeight: true,
});

// Same dual control as NavBarSubMenu: v-show is the fallback for browsers
// without the popover API, showPopover/hidePopover lift the panel into the
// top layer (out of the bar's `overflow: hidden`). Keep both in sync.
watch(
  () => props.open,
  (isOpen) => {
    nextTick(() => {
      const el = panelRef.value;
      if (!el) return;
      // Only while open, so a closed panel runs no Floating UI observers.
      anchorRef.value = isOpen ? findPanelAnchor(el, props.node) : null;
      if ('showPopover' in el) {
        try {
          if (isOpen) el.showPopover();
          else el.hidePopover();
        } catch {
          /* already in that state */
        }
      }
      if (isOpen) focusPanel(el);
    });
  },
  { immediate: true }
);
</script>

<template>
  <div
    v-show="open"
    :id="panelIdOf(node)"
    ref="panelRef"
    popover="manual"
    role="dialog"
    :aria-label="node.text"
    :style="floatingStyles"
    tabindex="-1"
    data-navbar-panel
    class="menubar-panel"
  >
    <!-- Content only while open: nothing of it mounts in a closed bar. -->
    <component :is="node.panel" v-if="open" @close="emit('close')" />
  </div>
</template>

<style scoped>
/* Placed by useFloatingPanel under the entry, spanning to the right; near the
   right edge it aligns to the entry's right edge instead. `fitHeight` hands
   it the room below the entry, so its content scrolls inside the viewport. */
.menubar-panel {
  position: fixed;
  inset: auto;
  box-sizing: border-box;
  width: max-content;
  max-width: min(90vw, var(--nxt-panel-width-2xl));
  max-height: var(--floating-available-height, calc(100vh - var(--nxt-space-xs)));
  margin: 0;
  padding: var(--nxt-space-2xs);
  border: var(--nxt-hairline) solid var(--nxt-border);
  background-color: var(--nxt-surface-raised-bg);
  color: var(--nxt-fg);
  border-radius: var(--nxt-radius-sm);
  box-shadow: var(--nxt-shadow-md);
  overflow: auto;
  overscroll-behavior: contain;
  outline: none;
}

/* The panel itself takes focus when its content has no `[autofocus]`. */
.menubar-panel:focus-visible {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-focus-ring);
  outline-offset: calc(var(--nxt-hairline) * -2);
}
</style>
